import React, { useEffect, useMemo, useState } from "react";

import styles from "./Buyurtmalar.module.css";
import {
  confirmAdminShopOrder,
  fetchAdminShopOrder,
  fetchAdminShopOrders,
  fetchShopProducts,
  updateAdminShopOrder,
} from "../shop/api";
import { formatPrice } from "../shop/utils";

const PRODUCT_PICK_LIMIT = 50;
const MONEY_QUANT = 1000;

function normDecStr(value) {
  if (value == null) return "";
  let next = String(value).trim().replace(",", ".");
  next = next.replace(/[^\d.-]/g, "");
  if (next.includes("-")) {
    next = (next.startsWith("-") ? "-" : "") + next.replace(/-/g, "");
  }
  const dotIndex = next.indexOf(".");
  if (dotIndex !== -1) {
    next = next.slice(0, dotIndex + 1) + next.slice(dotIndex + 1).replace(/\./g, "");
  }
  if (["", "-", ".", "-."].includes(next)) return "";
  return next;
}

function trimTrailingPriceZeros(value) {
  const normalized = normDecStr(value);
  if (!normalized) return "";
  if (!normalized.includes(".")) return normalized;
  return normalized.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0+$/u, "").replace(/\.$/u, "");
}

function toNum(value) {
  const parsed = Number(normDecStr(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function toFixed3Str(value) {
  const rounded = Math.round(Math.max(0, toNum(value)) * MONEY_QUANT);
  const integerPart = String(Math.floor(rounded / MONEY_QUANT));
  const fractionPart = String(rounded % MONEY_QUANT).padStart(3, "0");
  return `${integerPart}.${fractionPart}`;
}

function normalizeOrder(raw = {}) {
  return {
    group_token: raw.group_token || "",
    full_name: raw.full_name || "",
    phone: raw.phone || "",
    address: raw.address || "",
    comment: raw.comment || "",
    created_at: raw.created_at || "",
    sold_by_username: raw.sold_by_username || "",
    item_count: Number(raw.item_count || 0),
    total_quantity: Number(raw.total_quantity || 0),
    items: Array.isArray(raw.items)
      ? raw.items.map((item) => ({
          line_id: item.line_id,
          product_id: item.product_id,
          product_name: item.product_name || "",
          product_sku: item.product_sku || "",
          product_image: item.product_image || "",
          product_price: trimTrailingPriceZeros(item.product_price ?? "0"),
          product_stock_qty: Number(item.product_stock_qty || 0),
          quantity: Math.max(1, Math.floor(Number(item.quantity || 1))),
          has_shortage: Boolean(item.has_shortage),
        }))
      : [],
  };
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("uz-UZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BuyurtmalarPage() {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedToken, setSelectedToken] = useState("");
  const [draft, setDraft] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);

  const [mobileTab, setMobileTab] = useState("order"); // 'order' | 'search' | 'items'

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchTerm.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    async function loadOrders() {
      setListLoading(true);
      setError("");
      try {
        const data = await fetchAdminShopOrders({
          bucket: "pending",
          source: "bozor",
          search: searchQuery || undefined,
        });
        if (cancelled) return;

        const nextOrders = Array.isArray(data) ? data.map(normalizeOrder) : [];
        setOrders(nextOrders);
        setSelectedToken((current) => {
          if (current && nextOrders.some((item) => item.group_token === current)) {
            return current;
          }
          return nextOrders[0]?.group_token || "";
        });
        if (!nextOrders.length) {
          setDraft(null);
        }
      } catch (_) {
        if (!cancelled) {
          setOrders([]);
          setDraft(null);
          setError("Buyurtmalarni yuklashda xato yuz berdi.");
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    }

    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [searchQuery]);

  useEffect(() => {
    if (!selectedToken) {
      return;
    }

    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      setError("");
      try {
        const data = await fetchAdminShopOrder(selectedToken);
        if (!cancelled) {
          setDraft(normalizeOrder(data));
        }
      } catch (_) {
        if (!cancelled) {
          setError("Buyurtma tafsilotlarini olishda xato yuz berdi.");
          setDraft(null);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedToken]);

  useEffect(() => {
    const query = productQuery.trim();
    if (!query) {
      setProductResults([]);
      setProductLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setProductLoading(true);
      try {
        const data = await fetchShopProducts({ search: query, limit: PRODUCT_PICK_LIMIT });
        if (!cancelled) {
          setProductResults(Array.isArray(data) ? data.slice(0, PRODUCT_PICK_LIMIT) : []);
        }
      } catch (_) {
        if (!cancelled) {
          setProductResults([]);
        }
      } finally {
        if (!cancelled) {
          setProductLoading(false);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [productQuery]);

  useEffect(() => {
    setProductQuery("");
    setProductResults([]);
  }, [selectedToken]);

  const customerRows = useMemo(() => {
    if (!draft) return [];
    return [
      { label: "Ismi", value: draft.full_name || "-" },
      { label: "Telefon", value: draft.phone || "-" },
      { label: "Manzil", value: draft.address || "-" },
      { label: "Izoh", value: draft.comment || "-" },
    ];
  }, [draft]);

  const visibleProductResults = useMemo(() => productResults.slice(0, PRODUCT_PICK_LIMIT), [productResults]);
  const activeCustomerRows = customerRows.length
    ? customerRows
    : [
        { label: "Ismi", value: "-" },
        { label: "Telefon", value: "-" },
        { label: "Manzil", value: "-" },
        { label: "Izoh", value: "-" },
      ];
  const draftItemCount = draft?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const draftTotal = draft?.items.reduce((sum, item) => sum + toNum(item.product_price) * item.quantity, 0) || 0;

  const addProductToDraft = (product) => {
    setDraft((current) => {
      if (!current) return current;

      const existing = current.items.find((item) => item.product_id === product.id);
      if (existing) {
        return {
          ...current,
          items: current.items.map((item) =>
            item.product_id === product.id
              ? {
                  ...item,
                  quantity: item.quantity + 1,
                  has_shortage: item.quantity + 1 > item.product_stock_qty,
                }
              : item
          ),
        };
      }

      return {
        ...current,
        items: [
          ...current.items,
          {
            line_id: null,
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku || "",
            product_image: product.primary_image || "",
            product_price: trimTrailingPriceZeros(product.price ?? "0"),
            product_stock_qty: Number(product.stock_qty || 0),
            quantity: 1,
            has_shortage: Number(product.stock_qty || 0) < 1,
          },
        ],
      };
    });
    setProductQuery("");
    setProductResults([]);
  };

  const updateItemQuantity = (productId, nextQuantity) => {
    const parsed = Math.floor(Number(nextQuantity || 1));
    const safeQuantity = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.map((item) =>
          item.product_id === productId
            ? {
                ...item,
                quantity: safeQuantity,
                has_shortage: safeQuantity > item.product_stock_qty,
              }
            : item
        ),
      };
    });
  };

  const updateItemPrice = (productId, nextPrice) => {
    const safePrice = normDecStr(nextPrice);
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.map((item) =>
          item.product_id === productId
            ? {
                ...item,
                product_price: safePrice,
              }
            : item
        ),
      };
    });
  };

  const normalizeItemPrice = (productId) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.map((item) =>
          item.product_id === productId
            ? {
                ...item,
                product_price: trimTrailingPriceZeros(item.product_price),
              }
            : item
        ),
      };
    });
  };

  const removeItem = (productId) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        items: current.items.filter((item) => item.product_id !== productId),
      };
    });
  };

  const handleConfirm = async () => {
    if (!draft || !draft.items.length) {
      setError("Kamida bitta mahsulot qolishi kerak.");
      return;
    }

    setConfirming(true);
    setError("");
    setSuccess("");

    try {
      await confirmAdminShopOrder(draft.group_token, {
        items: draft.items.map((item) => ({
          product: item.product_id,
          quantity: item.quantity,
          unit_price: toFixed3Str(item.product_price),
        })),
      });

      setSuccess("Buyurtma tasdiqlandi va sotuvga yozildi.");
      const data = await fetchAdminShopOrders({
        bucket: "pending",
        source: "bozor",
        search: searchQuery || undefined,
      });
      const nextOrders = Array.isArray(data) ? data.map(normalizeOrder) : [];
      setOrders(nextOrders);
      const nextToken = nextOrders[0]?.group_token || "";
      setSelectedToken(nextToken);
      if (!nextToken) {
        setDraft(null);
      }
    } catch (saveError) {
      const serverMessage =
        saveError?.response?.data?.detail ||
        saveError?.response?.data?.items ||
        "Buyurtmani tasdiqlashda xato yuz berdi.";
      setError(Array.isArray(serverMessage) ? serverMessage.join(", ") : String(serverMessage));
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedToken || cancelling || confirming) {
      return;
    }

    const shouldCancel = window.confirm("Bu buyurtmani bekor qilmoqchimisiz?");
    if (!shouldCancel) {
      return;
    }

    setCancelling(true);
    setError("");
    setSuccess("");

    try {
      const currentOrder = normalizeOrder(await fetchAdminShopOrder(selectedToken));
      await updateAdminShopOrder(selectedToken, {
        source: "bozor",
        status: "cancelled",
        full_name: currentOrder.full_name,
        phone: currentOrder.phone,
        address: currentOrder.address,
        comment: currentOrder.comment,
        items: currentOrder.items.map((item) => ({
          product: item.product_id,
          quantity: item.quantity,
        })),
      });

      setSuccess("Buyurtma bekor qilindi.");
      const data = await fetchAdminShopOrders({
        bucket: "pending",
        source: "bozor",
        search: searchQuery || undefined,
      });
      const nextOrders = Array.isArray(data) ? data.map(normalizeOrder) : [];
      setOrders(nextOrders);
      const nextToken = nextOrders[0]?.group_token || "";
      setSelectedToken(nextToken);
      if (!nextToken) {
        setDraft(null);
      }
    } catch (saveError) {
      const serverMessage =
        saveError?.response?.data?.detail ||
        saveError?.response?.data?.items ||
        "Buyurtmani bekor qilishda xato yuz berdi.";
      setError(Array.isArray(serverMessage) ? serverMessage.join(", ") : String(serverMessage));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.mobTabs}>
        <button className={`${styles.mobTab}${mobileTab === 'order' ? ` ${styles.mobTabActive}` : ''}`} onClick={() => setMobileTab('order')} type="button">Buyurtmalar</button>
        <button className={`${styles.mobTab}${mobileTab === 'search' ? ` ${styles.mobTabActive}` : ''}`} onClick={() => setMobileTab('search')} type="button">Qidiruv</button>
        <button className={`${styles.mobTab}${mobileTab === 'items' ? ` ${styles.mobTabActive}` : ''}`} onClick={() => setMobileTab('items')} type="button">Mahsulotlar</button>
      </div>
      <section className={`${styles.headerCard} fr-card${mobileTab !== 'order' ? ` ${styles.mobHide}` : ''}`}>
        <div className={styles.editorToolbar}>
          <div>
            <h3>Buyurtma tafsiloti</h3>
            <p>Ism, telefon, manzil va izoh yuqorida bir qatorda turadi.</p>
          </div>
          {draft?.group_token ? <span className={styles.token}>#{draft.group_token}</span> : null}
        </div>

        {error ? <div className={styles.errorText}>{error}</div> : null}
        {success ? <div className={styles.successText}>{success}</div> : null}
        {detailLoading ? <div className={styles.empty}>Buyurtma yuklanmoqda...</div> : null}

        <div className={styles.customerGrid}>
          {activeCustomerRows.map((row) => (
            <div className={styles.customerCard} key={row.label}>
              <small>{row.label}</small>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      </section>

      <div className={styles.layout}>
        <section className={`${styles.orderList} fr-card${mobileTab !== 'order' ? ` ${styles.mobHide}` : ''}`}>
          <div className={styles.listHead}>
            <strong>Buyurtmalar ro'yxati</strong>
          </div>

          <input
            className={styles.input}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Ism yoki telefon raqam bilan qidiring"
            value={searchTerm}
          />

          <div className={styles.listBody}>
            {!listLoading && !orders.length ? <div className={styles.empty}>Kutilayotgan buyurtma yo'q.</div> : null}

            {orders.map((order) => (
              <button
                className={`${styles.orderCard} ${selectedToken === order.group_token ? styles.orderCardActive : ""}`}
                key={order.group_token}
                onClick={() => {
                  setSelectedToken(order.group_token);
                  setSuccess("");
                  setError("");
                }}
                type="button"
              >
                <div className={styles.orderCardTop}>
                  <strong>{order.full_name || "Ism yo'q"}</strong>
                  <span className={styles.dateChip}>{formatDate(order.created_at)}</span>
                </div>
                <div className={styles.orderMeta}>
                  <span>{order.phone || "Telefon yo'q"}</span>
                  <span>{order.total_quantity} ta</span>
                </div>
                <div className={styles.orderProducts}>
                  {order.items.slice(0, 3).map((item) => item.product_name).join(", ")}
                </div>
              </button>
            ))}

            {listLoading ? <div className={styles.empty}>Yuklanmoqda...</div> : null}
          </div>
        </section>

        <section className={`${styles.productPanel} fr-card${mobileTab !== 'search' ? ` ${styles.mobHide}` : ''}`}>
          <div className={styles.sectionHead}>
            <strong>Mahsulot qidirish</strong>
            <small>Buyurtmaga qo'shish</small>
          </div>

          <input
            className={styles.input}
            disabled={!draft || confirming || cancelling}
            onChange={(event) => setProductQuery(event.target.value)}
            placeholder={draft ? "Mahsulot nomi yoki seriyasi" : "Avval buyurtma tanlang"}
            value={productQuery}
          />

          {draft && productLoading ? <div className={styles.searchInfo}>Mahsulotlar qidirilmoqda...</div> : null}
          {draft && !productLoading && productQuery.trim() ? (
            <div className={styles.searchInfo}>Ko'pi bilan {PRODUCT_PICK_LIMIT} ta mahsulot chiqadi.</div>
          ) : null}

          <div className={styles.productGrid}>
            {visibleProductResults.map((product) => (
              <button
                className={styles.productCard}
                disabled={!draft || confirming || cancelling}
                key={product.id}
                onClick={() => addProductToDraft(product)}
                type="button"
              >
                <div className={styles.productCardMedia}>
                  {product.primary_image ? (
                    <img alt={product.name} src={product.primary_image} />
                  ) : (
                    <span>{String(product.name || "?").slice(0, 1)}</span>
                  )}
                </div>
                <div className={styles.productCardBody}>
                  <strong>{product.name}</strong>
                  <span>{product.sku || "SKU yo'q"}</span>
                  <small>
                    {formatPrice(product.price)} - {Number(product.stock_qty || 0)} ta
                  </small>
                </div>
              </button>
            ))}
          </div>

          {!draft ? <div className={styles.empty}>Mahsulot qo'shish uchun buyurtma tanlang.</div> : null}
          {draft && !productLoading && !visibleProductResults.length && productQuery.trim() ? (
            <div className={styles.empty}>Mahsulot topilmadi.</div>
          ) : null}
          {draft && !productLoading && !visibleProductResults.length && !productQuery.trim() ? (
            <div className={styles.empty}>Qidiruv orqali buyurtmaga yangi mahsulot qo'shing.</div>
          ) : null}
        </section>

        <section className={`${styles.itemsPanel} fr-card${mobileTab !== 'items' ? ` ${styles.mobHide}` : ''}`}>
          <div className={styles.sectionHead}>
            <strong>Buyurtma mahsulotlari</strong>
            <small>{draftItemCount} ta</small>
          </div>

          <div className={styles.itemsList}>
            {!draft ? <div className={styles.empty}>Tanlash uchun buyurtma yo'q.</div> : null}
            {draft && !draft.items.length ? <div className={styles.empty}>Mahsulot qolmagan.</div> : null}

            {draft?.items.map((item) => (
              <div className={styles.itemEditorRow} key={`${draft.group_token}-${item.product_id}`}>
                <div className={styles.saleCopy}>
                  <strong>{item.product_name}</strong>
                  <span>{item.product_sku || "SKU yo'q"}</span>
                  {item.has_shortage ? <em className={styles.shortage}>Omborda son yetmaydi</em> : null}
                </div>

                <div className={styles.itemControls}>
                  <div className={styles.itemField}>
                    <span>Soni</span>
                    <div className={styles.qtyBox}>
                      <button
                        disabled={confirming || cancelling || item.quantity <= 1}
                        onClick={() => updateItemQuantity(item.product_id, item.quantity - 1)}
                        type="button"
                      >
                        -
                      </button>
                      <input
                        disabled={confirming || cancelling}
                        min="1"
                        onChange={(event) => updateItemQuantity(item.product_id, event.target.value)}
                        type="number"
                        value={item.quantity}
                      />
                      <button
                        disabled={confirming || cancelling}
                        onClick={() => updateItemQuantity(item.product_id, item.quantity + 1)}
                        type="button"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className={styles.itemField}>
                    <span>Narxi</span>
                    <div className={styles.priceBox}>
                      <input
                        className={styles.priceInput}
                        disabled={confirming || cancelling}
                        inputMode="decimal"
                        onChange={(event) => updateItemPrice(item.product_id, event.target.value)}
                        onBlur={() => normalizeItemPrice(item.product_id)}
                        type="text"
                        value={item.product_price}
                      />
                      <small>so'm</small>
                    </div>
                  </div>

                  <div className={styles.itemTotals}>
                    <span>Jami</span>
                    <strong>{formatPrice(toNum(item.product_price) * item.quantity)}</strong>
                    <small>Omborda {item.product_stock_qty} ta</small>
                  </div>
                </div>

                <button
                  className={styles.removeButton}
                  disabled={confirming || cancelling}
                  onClick={() => removeItem(item.product_id)}
                  type="button"
                >
                  Olib tashlash
                </button>
              </div>
            ))}
          </div>

          <div className={styles.panelFooter}>
            <div className={styles.summary}>
              <strong>{draftItemCount} ta mahsulot</strong>
              <span>Jami: {formatPrice(draftTotal)}</span>
              <span>Bozor buyurtmasi tasdiqlansa sotuvga yoziladi.</span>
            </div>

            <div className={styles.footerActions}>
              <button
                className={styles.secondaryButton}
                disabled={confirming || cancelling || !selectedToken}
                onClick={handleCancel}
                type="button"
              >
                {cancelling ? "Bekor qilinmoqda..." : "Bekor qilish"}
              </button>
              <button
                className={styles.primaryButton}
                disabled={confirming || cancelling || !draft?.items.length}
                onClick={handleConfirm}
                type="button"
              >
                {confirming ? "Tasdiqlanmoqda..." : "Tasdiqlash"}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
